import { purgeMailClientCaches } from "@/lib/cache/purgeMailClientCaches";
import { MAIL_BUILD_VERSION, MAIL_BUILD_VERSION_LS_KEY } from "@/lib/cache/mailBuildVersion";

const REFRESH_ATTEMPTS_KEY = "mail_build_refresh_attempts";
const MAX_AUTO_REFRESH_ATTEMPTS = 2;
const UPDATE_RELOAD_TIMEOUT_MS = 10_000;

let hubHookInstalled = false;
let refreshInFlight = false;

function readMetaBuildVersion(): string {
  if (typeof document === "undefined") return MAIL_BUILD_VERSION;
  const meta = document.querySelector('meta[name="mail-build"]')?.getAttribute("content")?.trim();
  return meta || MAIL_BUILD_VERSION;
}

function readRefreshAttempts(): number {
  try {
    return Number.parseInt(sessionStorage.getItem(REFRESH_ATTEMPTS_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}

function writeRefreshAttempts(count: number): void {
  try {
    sessionStorage.setItem(REFRESH_ATTEMPTS_KEY, String(count));
  } catch {
    /* ignore */
  }
}

function clearRefreshAttempts(): void {
  try {
    sessionStorage.removeItem(REFRESH_ATTEMPTS_KEY);
  } catch {
    /* ignore */
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function purgeBrowserHttpCaches(): Promise<void> {
  if (!("caches" in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

async function onMailCachesMustRefresh(reason: string): Promise<void> {
  const targetBuild = readMetaBuildVersion();

  try {
    navigator.serviceWorker.controller?.postMessage({ type: "MAIL_PURGE_CACHES" });
  } catch {
    /* ignore */
  }

  await Promise.race([purgeBrowserHttpCaches(), sleep(4_000)]);
  await Promise.race([purgeMailClientCaches(), sleep(6_000)]);

  try {
    localStorage.setItem(MAIL_BUILD_VERSION_LS_KEY, targetBuild);
  } catch {
    /* ignore */
  }

  if (import.meta.env.DEV) {
    console.info("[Mail] Caches purgés:", reason, targetBuild);
  }
}

function isAuthModalOpen(): boolean {
  const modal = document.getElementById("authModal");
  return Boolean(modal?.classList.contains("flex"));
}

/** Première visite : enregistre la version sans bloquer le mount React. */
export function syncMailBuildVersionOnLaunch(): void {
  try {
    const meta = readMetaBuildVersion();
    const stored = localStorage.getItem(MAIL_BUILD_VERSION_LS_KEY);
    if (stored == null) {
      localStorage.setItem(MAIL_BUILD_VERSION_LS_KEY, meta);
      clearRefreshAttempts();
    }
  } catch {
    /* ignore */
  }
}

export function mailBuildNeedsUpdate(): boolean {
  if (isAuthModalOpen()) return false;
  const meta = readMetaBuildVersion();
  const bundleStale = meta !== MAIL_BUILD_VERSION;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(MAIL_BUILD_VERSION_LS_KEY);
  } catch {
    return false;
  }
  if (!bundleStale && stored === meta) return false;
  if (stored == null && !bundleStale) return false;
  return readRefreshAttempts() < MAX_AUTO_REFRESH_ATTEMPTS;
}

const PURGE_SEEN_KEY = "aurelia_purge_seen";
let purgeReloadArmed = false;

function cookiePurgeStamp(): string {
  const match = document.cookie.match(/(?:^|; )aurelia_purge=(\d+)/);
  return match ? match[1] : "";
}

async function purgeFromDesk(reason: string): Promise<void> {
  if (purgeReloadArmed) return;
  purgeReloadArmed = true;
  const stamp = cookiePurgeStamp() || String(Date.now());
  try {
    navigator.serviceWorker.controller?.postMessage({ type: "SUITE_PURGE" });
  } catch {
    /* ignore */
  }
  await onMailCachesMustRefresh(reason);
  try {
    localStorage.setItem(PURGE_SEEN_KEY, stamp);
  } catch {
    /* ignore */
  }
  window.setTimeout(() => window.location.reload(), 80);
}

export function installMailCacheGuard(): void {
  if (hubHookInstalled || typeof window === "undefined") return;
  hubHookInstalled = true;

  window.addEventListener("message", (event) => {
    if (event.data?.type !== "CLEAR_CACHE") return;
    void purgeFromDesk("hub_clear_cache");
  });

  const look = () => {
    const stamp = cookiePurgeStamp();
    if (!stamp) return;
    let seen = "";
    try {
      seen = localStorage.getItem(PURGE_SEEN_KEY) || "";
    } catch {
      seen = "";
    }
    if (stamp === seen) return;
    void purgeFromDesk("hub_purge_cookie");
  };
  look();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") look();
  });
  window.addEventListener("focus", look);
}

/** Force purge + reload (MAJ app) — un seul rechargement, timeout garanti. */
export async function hardRefreshMailApp(): Promise<void> {
  if (refreshInFlight) return;
  refreshInFlight = true;
  writeRefreshAttempts(readRefreshAttempts() + 1);

  const reload = () => {
    window.location.reload();
  };

  try {
    await Promise.race([
      onMailCachesMustRefresh("hard_refresh"),
      sleep(UPDATE_RELOAD_TIMEOUT_MS),
    ]);
    reload();
  } catch {
    refreshInFlight = false;
    reload();
  }
}

/** Console : `mailForceRefresh()` pour forcer purge + reload. */
export function exposeMailDebugHelpers(): void {
  if (typeof window === "undefined") return;

  const win = window as Window & {
    mailForceRefresh?: () => Promise<void>;
    mailClearClientCaches?: () => Promise<void>;
  };

  win.mailForceRefresh = () => hardRefreshMailApp();
  win.mailClearClientCaches = async () => {
    await purgeMailClientCaches();
    window.location.reload();
  };
}

declare global {
  interface Window {
    mailForceRefresh?: () => Promise<void>;
    mailClearClientCaches?: () => Promise<void>;
  }
}
